import { computed, inject, provide, type ComputedRef, type InjectionKey } from 'vue'
import type { WorkspacePermission } from '@/types/permissions'

const PERMISSIONS_KEY: InjectionKey<ComputedRef<string[]>> = Symbol('workspacePermissions')

/**
 * Whether a workspace's permission list names `permission`. Typed by scope, so an organization
 * key does not compile here: it is never in a workspace role's list.
 */
export function holdsWorkspacePermission(
  permissions: readonly string[] | null | undefined,
  permission: WorkspacePermission,
): boolean {
  return permissions?.includes(permission) ?? false
}

/**
 * Provide workspace permissions from the layout level.
 * Called once in AssetDetailLayout with workspace permissions.
 */
export function provideAssetPermissions(permissions: ComputedRef<string[]>) {
  provide(PERMISSIONS_KEY, permissions)

  return {
    canEdit: computed(() => holdsWorkspacePermission(permissions.value, 'assets.asset.edit')),
    canDelete: computed(() => holdsWorkspacePermission(permissions.value, 'assets.asset.delete')),
    canTrash: computed(() => holdsWorkspacePermission(permissions.value, 'assets.asset.trash')),
  }
}

/**
 * Inject workspace permissions in child views.
 * Returns permission check helpers based on the provided workspace permissions.
 */
export function useAssetPermissions() {
  const permissions = inject(PERMISSIONS_KEY)

  if (!permissions) {
    if (import.meta.env.DEV) {
      console.warn('[useAssetPermissions] No provider found — returning no-permission fallback')
    }
    return buildPermissionHelpers(computed<string[]>(() => []))
  }

  return buildPermissionHelpers(permissions)
}

function buildPermissionHelpers(permissions: ComputedRef<string[]>) {
  const can = (permission: WorkspacePermission) => holdsWorkspacePermission(permissions.value, permission)

  return {
    can,
    canEdit: computed(() => can('assets.asset.edit')),
    canDelete: computed(() => can('assets.asset.delete')),
    canTrash: computed(() => can('assets.asset.trash')),
    canCreate: computed(() => can('assets.asset.create')),
    canExport: computed(() => can('assets.asset.export')),
    canManageWorkspace: computed(() => can('assets.workspace.manage')),
    canDeleteWorkspace: computed(() => can('assets.workspace.delete')),
    canViewAnalytics: computed(() => can('assets.asset.view_analytics')),
    canModerate: computed(() => can('assets.asset.moderate')),
  }
}
